import { Card, Stack, Table, Button, TableBody, Typography, TableContainer } from '@mui/material';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import TableHeadCustom from 'src/components/table/table-head-custom';
import TableNoData from 'src/components/table/table-no-data';

import { useBoolean } from 'src/hooks/use-boolean';
import { useState, useEffect, useCallback } from 'react';
import useBankOptions from 'src/hooks/use-bank-option';
import { toastError } from 'src/utils/format-error';
import axios, { endpoints } from 'src/utils/axios';
import TableLoading from 'src/components/table/table-loading';
import { BranchType } from 'src/type/branch';
import { Pagination } from 'src/type/utils';
import SettingBranchFormDialog from './setting-branch-form-dialog';
import SettingBranchRow from './setting-branch-row';

const tableHead = [
  { id: 'code', label: 'สาขา' },
  { id: 'action', width: 60 },
];

export default function SettingBranch() {
  const form = useBoolean();
  const loading = useBoolean();
  const initialize = useBoolean(true);
  const [branches, setBranches] = useState(Array<BranchType>());
  const reload = useBoolean();
  const [focusBranch, setFocusBranch] = useState<BranchType | null>(null);
  const { bankOptions } = useBankOptions();

  useEffect(() => {
    load();
  }, [reload.value]);

  const load = async () => {
    try {
      const { data } = await axios.get<Pagination<BranchType>>(endpoints.branch.get, {
        params: { limit: 30 },
      });
      setBranches(data.results);
    } catch (error) {
      toastError(error);
    } finally {
      initialize.onFalse();
    }
  };

  const forceReload = useCallback(() => {
    reload.onToggle();
  }, []);

  const handleOpenCreateForm = useCallback(() => {
    setFocusBranch(null);
    form.onTrue();
  }, []);

  const handleOpenEditForm = useCallback((account: BranchType) => {
    setFocusBranch(account);
    form.onTrue();
  }, []);

  const handleCloseForm = useCallback(() => {
    setFocusBranch(null);
    form.onFalse();
  }, []);

  const handleDeleteBank = useCallback(async (account: BranchType) => {
    try {
      loading.onTrue();
      await axios.delete(endpoints.backAccount.delete(account.id));
      forceReload();
    } catch (error) {
      toastError(error);
    } finally {
      loading.onFalse();
    }
  }, []);

  return (
    <>
      <Card sx={{ py: 5, px: 3 }}>
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="h4" gutterBottom>
              สาขา
            </Typography>
            <Button
              variant="contained"
              startIcon={<Iconify icon="mingcute:add-line" />}
              size="medium"
              onClick={handleOpenCreateForm}
            >
              เพิ่มสาขา
            </Button>
          </Stack>

          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <Scrollbar>
              <Table sx={{ minWidth: 800 }}>
                <TableHeadCustom headLabel={tableHead} />

                <TableBody>
                  <TableLoading loading={initialize.value} rows={5}>
                    {branches.map((row) => (
                      <SettingBranchRow
                        key={row.id}
                        row={row}
                        loading={loading.value}
                        onEdit={() => handleOpenEditForm(row)}
                        handleDelete={() => handleDeleteBank(row)}
                      />
                    ))}

                    <TableNoData notFound={!branches.length} />
                  </TableLoading>
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>
        </Stack>
      </Card>
      <SettingBranchFormDialog
        open={form.value}
        onClose={handleCloseForm}
        forceReload={forceReload}
        branch={focusBranch}
        bankOptions={bankOptions}
      />
    </>
  );
}
